import { OmDialog } from '@owma/kit'
import { useState } from 'react'
import Avatar from '../../Avatar'
import AttendeeMeetingAvailableListModal from './AttendeeMeetingAvailableListModal'
import styles from './AttendeeStatusList.module.scss'
import { useAttendeesWithStatus } from './hooks/useAttendeesWithStatus'
import type { Attendee, BusyTimes } from './types'
import type { TimeRange } from './utils/SchedulingUtils'
import { getAttendeeStatusIcon } from './utils/attendeeStatus'

interface AttendeeStatusListProps {
  attendees: Attendee[]
  busyTimes?: BusyTimes
  selectedTimeRange?: (TimeRange & { allday?: boolean }) | null
  selectedTimeRangeList?: (TimeRange & { allday?: boolean })[] | null
  ownerHasConflict?: boolean
  maxVisible?: number
}

const AttendeeStatusList = ({
  attendees,
  busyTimes = {},
  selectedTimeRange = null,
  selectedTimeRangeList = null,
  ownerHasConflict = false,
  maxVisible = 5,
}: AttendeeStatusListProps) => {
  const [showModal, setShowModal] = useState(false)

  // Compute attendee status using custom hook
  const attendeesWithStatus = useAttendeesWithStatus(
    attendees,
    busyTimes,
    selectedTimeRange,
    selectedTimeRangeList,
    ownerHasConflict,
  )

  // Split attendees into visible and hidden
  // When there are more attendees than maxVisible, reserve one slot for moreIndicator
  const shouldShowMore = attendeesWithStatus.length > maxVisible
  const visibleAttendees = shouldShowMore
    ? attendeesWithStatus.slice(0, maxVisible - 1)
    : attendeesWithStatus
  const hiddenCount = shouldShowMore ? attendeesWithStatus.length - (maxVisible - 1) : 0

  return (
    <div className={styles.container}>
      <div className={styles.avatarGroup} onClick={() => setShowModal(true)}>
        {visibleAttendees.map((attendee) => {
          const email = attendee.email
          const busyData = busyTimes[email]
          const { statusIcon, statusAlt } = getAttendeeStatusIcon(
            attendee,
            selectedTimeRange,
            selectedTimeRangeList,
            busyData,
          )

          return (
            <div key={attendee.email} className={styles.avatarWrapper}>
              <div className={styles.avatar}>
                <Avatar name={attendee.name || attendee.email} email={attendee.email} skipLogo />
              </div>
              {/* Show status icon based on conflict detection */}
              {statusIcon && (
                <div className={styles.statusIcon}>
                  <img src={statusIcon} alt={statusAlt} />
                </div>
              )}
            </div>
          )
        })}

        {/* Show +N indicator if there are hidden attendees */}
        {hiddenCount > 0 && (
          <div className={styles.moreIndicator}>
            <span>+{hiddenCount}</span>
          </div>
        )}
      </div>
      <OmDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        width={400}
        destroyOnHidden
        closable={false}
        contentStyle={{ padding: 0, overflow: 'hidden' }}
      >
        <AttendeeMeetingAvailableListModal
          attendees={attendees}
          busyTimes={busyTimes}
          selectedTimeRange={selectedTimeRange}
          selectedTimeRangeList={selectedTimeRangeList}
          ownerHasConflict={ownerHasConflict}
          onClose={() => setShowModal(false)}
        />
      </OmDialog>
    </div>
  )
}

export default AttendeeStatusList
